import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { onSnapshot, query, where, orderBy, type Unsubscribe } from 'firebase/firestore'
import type { ProjectDoc, ProjectEntity } from '@common/models/project/project_model'
import { toProjectEntity } from '@common/models/project/project_model'
import { refs } from '@common/models/base/firestore-refs'
import { useSchoolAccount } from './SchoolAccountContext'

type ProjectContextType = {
  projects: ProjectEntity[];
  isLoading: boolean;
  error: Error | null;
};

const ProjectContext = createContext<ProjectContextType>({
  projects: [],
  isLoading: true,
  error: null,
})

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const { schoolAccount, isLoading: accountLoading } = useSchoolAccount();
  const [projects, setProjects] = useState<ProjectEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (accountLoading || !schoolAccount) {
      setIsLoading(accountLoading);
      if (!accountLoading && !schoolAccount) {
        setProjects([]);
        setError(null);
        setIsLoading(false);
      }
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const schoolAccountId = schoolAccount.id
      const projectCollectionRef = refs.schoolAccountProjects(schoolAccountId)

      // is_deleted == false の条件でフィルタリング
      const q = query(
        projectCollectionRef,
        where('is_deleted', '==', false),
        orderBy('created_at', 'desc')
      );

      // リアルタイム監視
      const unsubscribe: Unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const fetchedProjects: ProjectEntity[] = [];

          snapshot.docs.forEach((docSnap) => {
            try {
              const project = toProjectEntity(docSnap.id, docSnap.data() as ProjectDoc, docSnap.ref.path)
              if (project) {
                project.school_account_id = schoolAccountId
                fetchedProjects.push(project);
              }
            } catch (err) {
              console.error('Project parse error:', err);
            }
          });

          setProjects(fetchedProjects);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Failed to subscribe projects:', err);
          setError(err instanceof Error ? err : new Error('案件の取得に失敗しました'));
          setIsLoading(false);
        }
      );

      // クリーンアップ関数
      return () => {
        unsubscribe();
      };
    } catch (err) {
      console.error('Project subscription setup error:', err);
      setError(err instanceof Error ? err : new Error('案件の監視設定に失敗しました'));
      setIsLoading(false);
      return undefined;
    }
  }, [schoolAccount, accountLoading]);

  return (
    <ProjectContext.Provider value={{ projects, isLoading, error }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
